import React from 'react';
import { IntlFormatters, useIntl } from 'react-intl';
import ReactDOM from 'react-dom';
import uuid from 'uuid/v4';
import styles from './use-aria-live.scss';
import useDiv from './use-div';
import { useTimeout } from './use-timeout';

type FormatMessageParameters = Parameters<IntlFormatters['formatMessage']>

export type MessageValues = FormatMessageParameters[1]

type AnnounceFn = (
  message: FormatMessageParameters[0],
  values?: MessageValues
) => void

const ariaContext = React.createContext<AnnounceFn | undefined>(undefined);

export function useAriaLive(): AnnounceFn {
  const announce = React.useContext(ariaContext);
  if (!announce) {
    throw new Error('Missing AriaLiveContextProvider');
  }

  return announce;
}

// This component is rendered as part of `<HSIntlProvider`, so we shouldn't need
// it anywhere else in the app.
export function AriaLiveContextProvider({ children }: React.PropsWithChildren<{}>) {
  const intl = useIntl();
  const portalDiv = useDiv();
  const setTimeout = useTimeout();

  type Announcement = {
    id: string,
    text: string | React.ReactNodeArray,
  }
  const [announcements, setAnnouncements] = React.useState<Announcement[]>([]);

  const remove = React.useCallback((announcement: Announcement) => {
    setAnnouncements((all) => {
      return all.filter(
        (item) => item.id !== announcement.id,
      );
    });
  }, []);

  const announce: AnnounceFn = React.useCallback((message, values) => {
    // I built the system for displaying messages, but I don't actually know
    // what the messages should be yet. When the Dropbox SME gets back to me
    // I'll remove this check.
    // https://dropbox.slack.com/archives/C09KKPK7U/p1590016040091100
    if (process.env.NODE_ENV !== 'test' && !process.env.IS_STORYBOOK) {
      return;
    }

    const id = uuid();
    const text = intl.formatMessage(message, values);
    const announcement = {
      id,
      text,
    };
    setTimeout(remove, 10000, announcement);

    setAnnouncements((all) => {
      return [...all, announcement];
    });
  }, [intl, remove, setTimeout]);

  const liveRegion = ReactDOM.createPortal(
    (
      <div
        className={styles.ariaLive}
        data-qa-ref="aria-live"
        aria-live="assertive"
        aria-relevant="additions"
        aria-atomic="true"
      >
        {announcements.map((a) => (
          <div data-qa-ref="aria-announcement" key={a.id}>{a.text}</div>
        ))}
      </div>
    ),
    portalDiv,
  );

  return (
    <ariaContext.Provider value={announce}>
      {children}
      {liveRegion}
    </ariaContext.Provider>
  );
}
